// 2houses "conversations" PDF export → canonical corpus.csv.
//
// Direct port of evals/adapters/two_houses.py — keep the parsing in lockstep
// so a 2houses PDF run through the browser yields the same canonical rows as
// the desktop / Python adapter (parity is validated in two_houses.test.ts
// against the Python reference from the same tiny-sample.pdf).
//
// The 2houses export is highly regular and — unlike OFW — carries NO running
// header/footer and NO page-boundary line reprints, so message bodies flow
// seamlessly across pages and there is no dedup pass:
//
//     2houses conversations                            <- title (preamble)
//     Exported on Friday 26 June 2026 10 h 10          <- export stamp (preamble)
//     Subject : <thread name>                          <- thread header
//     <Sender> said: <Weekday> <DD> <Month> <YYYY> <HH> h <MM>   <- message header
//     <body, free multi-line text — may span page breaks cleanly>
//     ...
//
// It also carries NO timezone, so the caller supplies one (default
// Australia/Melbourne, AEST/AEDT); each naive wall-clock timestamp is localized
// to that IANA zone (DST-aware) and stored as UTC ISO, matching the OFW / Civil
// Communicator timestamp shape.
import { DateTime, IANAZone } from "luxon";

import { sanitizeActor, sanitizeBody } from "./_normalize";
import { extractPdfTextEol, type PdfExtractProgress } from "./_pdf";

export const PARSER_VERSION = "0.1.0";
export const ADAPTER_NAME = "two-houses";

// 2houses exports carry no timezone; co-parenting cases are local. Default to
// Victoria's zone (AEST/AEDT). Override per-case via the ingest option.
// Lockstep with two_houses.py DEFAULT_TIMEZONE.
export const DEFAULT_TIMEZONE = "Australia/Melbourne";

// Full month name → 1-indexed month number. Lockstep with two_houses.py _MONTHS.
const MONTHS: Record<string, number> = {
  January: 1,
  February: 2,
  March: 3,
  April: 4,
  May: 5,
  June: 6,
  July: 7,
  August: 8,
  September: 9,
  October: 10,
  November: 11,
  December: 12,
};

// ISO weekday index (luxon .weekday: 1=Monday..7=Sunday) → English name. Used as
// the redundant-weekday checksum below. Lockstep with two_houses.py _WEEKDAYS.
const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

// Full-line message header. The trailing "<HH> h <MM>" time (a localized 2houses
// quirk) plus "said:" makes this distinctive enough that a body line won't match
// unless it is itself a verbatim header (indistinguishable, and acceptable).
// Non-global: applied per line. Lockstep with two_houses.py _HEADER_RE.
const HEADER_RE =
  /^(.+?) said: ([A-Za-z]+) (\d{1,2}) ([A-Za-z]+) (\d{4}) (\d{1,2}) h (\d{2})$/;
const SUBJECT_RE = /^Subject : (.+)$/;

// pdf.js (the browser extractor) splits a header's right-aligned timestamp onto
// its own line via _pdf.ts's colGap column rule — a rule that's REQUIRED for
// OFW covers ("Timezone:…" + a right-hand contact block). pdfplumber
// (Python/desktop) keeps "<sender> said: <timestamp>" on one line — the shape
// HEADER_RE, the fingerprint, and the fixtures all assume. So the same real
// 2houses export parses on desktop but fails in the browser. Rejoin the split
// so both extractors converge on one shape before scoring/parsing. No-op on
// already-joined text (desktop path, fixtures). TS-only by design: the Python
// path never splits, so two_houses.py needs no twin — raw parsing is allowed to
// diverge per-extractor as long as the canonical rows come out identical.
const SPLIT_SAID_HEADER_RE =
  / said:[ \t]*\n[ \t]*(?=[A-Za-z]+ \d{1,2} [A-Za-z]+ \d{4} \d{1,2} h \d{2}\b)/g;

/** Undo pdf.js's right-aligned-timestamp line split in 2houses headers. */
export function mergeSplitSaidHeaders(text: string): string {
  return text.replace(SPLIT_SAID_HEADER_RE, " said: ");
}

export class MalformedTwoHousesError extends Error {
  constructor(msg: string) {
    super(msg);
    this.name = "MalformedTwoHousesError";
  }
}

// --- Canonical row + metadata shapes (6-column, subject-bearing — same as OFW) ---

export interface CanonicalRow {
  timestamp: string;
  sender: string;
  recipient: string;
  subject: string;
  body: string;
  attachments: string;
}

export interface SourceMetadata {
  source: string;
  message_number: number;
  subject: string;
  sender_role: "party" | "other";
  attachments_structured: never[];
}

export interface IngestStats {
  party_a: string;
  party_b: string;
  out_of_set_senders: string[];
  message_count: number;
  // 2houses has no moderation/rejection concept (every message is real / sent),
  // but the shared stats contract carries accepted/rejected — all accepted.
  accepted_count: number;
  rejected_count: number;
  non_printables_stripped: number;
  date_range: { start: string | null; end: string | null };
  // The caller-supplied (or defaulted) IANA source zone for the naive
  // wall-clock timestamps.
  timezone: string;
  // True when no explicit zone was supplied and DEFAULT_TIMEZONE was used
  // (2houses carries no zone). Surfaced so the UI can flag/verify it, mirroring
  // the OFW cover-less "timezone assumed" affordance. Not part of the Python
  // stats contract — an extra field the UI may read.
  timezone_assumed: boolean;
  // PDF page count from the source export (parity with the OFW adapter — the
  // cost screen prefers this over a token-derived estimate). Attached by
  // ingestTwoHousesFromPages, which sees the page list.
  page_count?: number;
}

export interface CaseMeta {
  id: string;
  privacy_class: string;
  prompt_vars: {
    party_a: string;
    party_b: string;
    format: string;
    time_period: string;
    context: string;
  };
  known_patterns: string[];
  notes: string;
  // 2houses carries no zone, so this is the caller-supplied/default zone rather
  // than a parser inference — but it surfaces through the same triple OFW writes
  // so the frontend doesn't flip on a "UTC (predicted)" badge. Lockstep with
  // two_houses.py _build_case_meta.
  source_timezone?: string;
  predicted_timezone?: string;
  user_timezone?: string;
}

export interface IngestResult {
  rows: CanonicalRow[];
  source_metas: SourceMetadata[];
  stats: IngestStats;
  csv: string;
  case_meta: CaseMeta;
  // Non-fatal ingest warnings (e.g. the assumed-timezone notice, or the count
  // of header-shaped look-alike lines kept as body). Mirrors the ingest.log
  // lines the Python adapter writes.
  warnings: string[];
}

export interface ParsedMessage {
  sender: string;
  // Naive local wall-clock components (no zone yet).
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  subject: string;
  body: string;
}

// --- Stage 1-2: concatenate pages (no header/footer stripping needed) ---

// 2houses has clean cross-page continuation and needs no page-boundary dedup,
// so — unlike OFW — there is no per-line page map to thread through. Lockstep
// with two_houses.py _concatenate_pages.
function concatenatePages(pages: string[]): string {
  return pages.join("\n");
}

// --- Stage 3: parse messages ---

// Validate a matched header and return its naive local datetime components, or
// null. A genuine 2houses header carries a *redundant* weekday name; we use it
// as a checksum: a header-shaped line is a real message boundary only when its
// month is a known English month, its date/time is in range, AND the named
// weekday matches the date. This rejects — with no crash and no message split —
// three classes of look-alike: a body-embedded quoted/typed header, an
// out-of-range or garbled date, and a non-English-locale header. Lockstep with
// two_houses.py _header_datetime.
function headerDatetime(h: RegExpMatchArray): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
} | null {
  const wd = h[2];
  const day = parseInt(h[3], 10);
  const monName = h[4];
  const year = parseInt(h[5], 10);
  const hour = parseInt(h[6], 10);
  const minute = parseInt(h[7], 10);
  const month = MONTHS[monName];
  if (month === undefined) return null;
  // luxon marks an out-of-range calendar date/time invalid (never rolls over),
  // matching Python's datetime() ValueError → None.
  const dt = DateTime.utc(year, month, day, hour, minute);
  if (!dt.isValid) return null;
  // weekday checksum (luxon .weekday: 1=Monday..7=Sunday).
  if (WEEKDAYS[dt.weekday - 1] !== wd) return null;
  return { year, month, day, hour, minute };
}

// Walk lines, tracking the current Subject thread and accumulating each
// message's body until the next *validated* header / subject. Returns the
// parsed messages and `keptAsBody`: the header-shaped lines that failed the
// weekday checksum and were preserved as body text rather than split off or
// dropped (the caller logs the count for transparency). Lockstep with
// two_houses.py _parse_messages.
export function parseMessages(combinedText: string): {
  messages: ParsedMessage[];
  keptAsBody: string[];
} {
  const messages: ParsedMessage[] = [];
  const keptAsBody: string[] = [];
  let currentSubject = "";
  let cur:
    | {
        sender: string;
        dt: { year: number; month: number; day: number; hour: number; minute: number };
        subject: string;
        body: string[];
      }
    | null = null;
  const lines = mergeSplitSaidHeaders(combinedText).split("\n");

  const flush = (): void => {
    if (cur !== null) {
      const body = cur.body.join("\n").trim();
      messages.push({
        sender: cur.sender,
        year: cur.dt.year,
        month: cur.dt.month,
        day: cur.dt.day,
        hour: cur.dt.hour,
        minute: cur.dt.minute,
        subject: cur.subject,
        body,
      });
      cur = null;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const h = line.match(HEADER_RE);
    if (h !== null) {
      const dt = headerDatetime(h);
      if (dt !== null) {
        flush();
        cur = {
          sender: h[1].trim(),
          dt,
          subject: currentSubject,
          body: [],
        };
        continue;
      }
      // Header-shaped but invalid (look-alike / garble / non-English): keep it
      // verbatim as body text — never split a phantom message, never silently
      // merge a real header, never crash.
      keptAsBody.push(line);
      if (cur !== null) cur.body.push(line);
      continue;
    }
    const s = line.match(SUBJECT_RE);
    if (s !== null) {
      // A real thread header is immediately followed by a *valid* message
      // header; a body line that merely starts "Subject :" is not, and must not
      // re-point the running thread.
      const nxt = i + 1 < lines.length ? lines[i + 1] : "";
      const nh = nxt.match(HEADER_RE);
      if (nh !== null && headerDatetime(nh) !== null) {
        currentSubject = s[1].trim();
        continue;
      }
      // else: ordinary body text — fall through.
    }
    if (cur !== null) cur.body.push(line);
  }
  flush();
  return { messages, keptAsBody };
}

// --- Stage 4: timestamps + canonical rows ---

// Localize a naive wall-clock datetime to `tzName` (DST-aware) and render UTC
// ISO-8601 with a 'Z' suffix. During the autumn fall-back the local wall clock
// is ambiguous; 2houses records no offset, so the instant is genuinely
// unknowable. luxon resolves the overlap to the earlier (pre-transition) offset
// — matching the Python adapter's explicit fold=0. Lockstep with
// two_houses.py _to_utc_iso.
export function toUtcIso(
  dt: { year: number; month: number; day: number; hour: number; minute: number },
  tzName: string,
): string {
  const local = DateTime.fromObject(
    {
      year: dt.year,
      month: dt.month,
      day: dt.day,
      hour: dt.hour,
      minute: dt.minute,
      second: 0,
    },
    { zone: tzName },
  );
  if (!local.isValid) {
    throw new MalformedTwoHousesError(
      `bad datetime in ${tzName}: ${local.invalidExplanation ?? local.invalidReason}`,
    );
  }
  return local.toUTC().toFormat("yyyy-LL-dd'T'HH:mm:ss'Z'");
}

// Return [party_a, party_b, out_of_set_senders]. The two most frequent senders
// become the declared parties; ties break on first-encountered order. 2houses
// is a two-party app, so out_of_set is normally empty. Lockstep with
// two_houses.py _determine_declared_parties.
export function determineDeclaredParties(
  messages: ParsedMessage[],
): [string, string, string[]] {
  if (messages.length === 0) return ["", "", []];
  // Rank SANITIZED sender names — rows compare their sanitized sender
  // against this declared set, so ranking raw names would make a control-
  // char-bearing party unmatchable (every row demoted to role "other").
  // Lockstep with two_houses.py _determine_declared_parties.
  const counts = new Map<string, number>();
  const firstSeen = new Map<string, number>();
  messages.forEach((m, idx) => {
    const name = sanitizeActor(m.sender)[0];
    counts.set(name, (counts.get(name) ?? 0) + 1);
    if (!firstSeen.has(name)) firstSeen.set(name, idx);
  });
  const ordered = [...counts.entries()].sort((a, b) => {
    if (b[1] !== a[1]) return b[1] - a[1];
    return (firstSeen.get(a[0]) ?? 0) - (firstSeen.get(b[0]) ?? 0);
  });
  const partyA = ordered[0]?.[0] ?? "";
  const partyB = ordered[1]?.[0] ?? "";
  const declared = new Set([partyA, partyB]);
  // counts keys are already sanitized, so outOfSet compares consistently.
  const outOfSet = [...counts.keys()].filter((n) => !declared.has(n)).sort();
  return [partyA, partyB, outOfSet];
}

function buildRowsAndMetadata(
  messages: ParsedMessage[],
  sourceTz: string,
  timezoneAssumed: boolean,
): { rows: CanonicalRow[]; metas: SourceMetadata[]; stats: IngestStats } {
  const [partyA, partyB, outOfSet] = determineDeclaredParties(messages);
  const declared = new Set([partyA, partyB]);
  let nonPrintables = 0;

  type Provisional = { ts: string; row: CanonicalRow; meta: SourceMetadata };
  const provisional: Provisional[] = [];
  for (const m of messages) {
    const ts = toUtcIso(
      { year: m.year, month: m.month, day: m.day, hour: m.hour, minute: m.minute },
      sourceTz,
    );
    const [body, nBody] = sanitizeBody(m.body);
    const [sender, nSender] = sanitizeActor(m.sender);
    const [subject, nSubj] = sanitizeBody(m.subject);
    nonPrintables += nBody + nSender + nSubj;

    let senderRole: "party" | "other";
    let recipient: string;
    if (declared.has(sender)) {
      senderRole = "party";
      recipient = sender === partyA ? partyB : partyA;
    } else {
      senderRole = "other";
      recipient = "";
    }

    provisional.push({
      ts,
      row: { timestamp: ts, sender, recipient, subject, body, attachments: "" },
      meta: {
        source: ADAPTER_NAME,
        message_number: 0,
        subject,
        sender_role: senderRole,
        attachments_structured: [],
      },
    });
  }

  provisional.sort((a, b) => (a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : 0));
  const rows = provisional.map((p) => p.row);
  const metas = provisional.map((p, i) => ({ ...p.meta, message_number: i + 1 }));

  const dateRange = {
    start: rows.length > 0 ? rows[0].timestamp : null,
    end: rows.length > 0 ? rows[rows.length - 1].timestamp : null,
  };

  const stats: IngestStats = {
    party_a: partyA,
    party_b: partyB,
    out_of_set_senders: outOfSet,
    message_count: rows.length,
    accepted_count: rows.length,
    rejected_count: 0,
    non_printables_stripped: nonPrintables,
    date_range: dateRange,
    timezone: sourceTz,
    timezone_assumed: timezoneAssumed,
  };
  return { rows, metas, stats };
}

// --- CSV serialization (6-column canonical, RFC 4180 quoting) ---

const CANONICAL_HEADERS = [
  "timestamp",
  "sender",
  "recipient",
  "subject",
  "body",
  "attachments",
] as const;

function csvEscape(cell: string): string {
  if (/[",\n\r]/.test(cell)) {
    return `"${cell.replace(/"/g, '""')}"`;
  }
  return cell;
}

function rowsToCsv(rows: CanonicalRow[]): string {
  const header = CANONICAL_HEADERS.join(",") + "\n";
  if (rows.length === 0) return header;
  const body = rows
    .map((r) => CANONICAL_HEADERS.map((h) => csvEscape(r[h])).join(","))
    .join("\n");
  return header + body + "\n";
}

// --- Case meta builder ---

const TODO_CONTEXT_SENTINEL =
  "TODO: describe the context (e.g. 'their 9-year-old son Murphy')";
const TODO_NOTES_SENTINEL = "TODO: optional freeform notes about this corpus";

function formatTimePeriod(startIso: string, endIso: string): string {
  if (!startIso || !endIso) return "";
  const a = DateTime.fromISO(startIso, { zone: "utc" }).toFormat("LLL yyyy");
  const b = DateTime.fromISO(endIso, { zone: "utc" }).toFormat("LLL yyyy");
  return a === b ? a : `${a} – ${b}`;
}

function buildCaseMeta(corpusId: string, stats: IngestStats): CaseMeta {
  const meta: CaseMeta = {
    id: corpusId,
    privacy_class: "private",
    prompt_vars: {
      party_a: stats.party_a,
      party_b: stats.party_b,
      format:
        "2houses conversations PDF export, canonicalized to CSV (schema v1)",
      time_period: formatTimePeriod(
        stats.date_range.start ?? "",
        stats.date_range.end ?? "",
      ),
      context: TODO_CONTEXT_SENTINEL,
    },
    known_patterns: [],
    notes: TODO_NOTES_SENTINEL,
  };
  if (stats.timezone) {
    meta.source_timezone = stats.timezone;
    meta.predicted_timezone = stats.timezone;
    meta.user_timezone = stats.timezone;
  }
  return meta;
}

// --- Public entry points ---

export interface IngestOptions {
  /** Slug for the case (sets meta.id). Required by the cloud-server. */
  corpusId: string;
  /** Optional progress callback (per-page during pdf extraction). */
  onProgress?: (p: PdfExtractProgress) => void;
  /**
   * The IANA zone the export's naive wall-clock timestamps are interpreted in.
   * 2houses carries no zone, so when omitted we fall back to DEFAULT_TIMEZONE
   * and flag the assumption loudly (stats.timezone_assumed + a warning), mirroring
   * the OFW cover-less path.
   */
  defaultTimezone?: string;
}

/**
 * Pure-text parsing path: takes already-extracted page texts and returns the
 * canonical IngestResult. Exposed so unit tests can exercise the parser without
 * running pdf.js in Node. The PDF extraction step is the shared _pdf.ts path
 * used by the OFW / CC adapters.
 *
 * Throws MalformedTwoHousesError if no valid 2houses message headers are found,
 * or ValueError-style Error if the supplied timezone is unknown.
 */
export function ingestTwoHousesFromPages(
  pagesRaw: string[],
  opts: { corpusId: string; defaultTimezone?: string },
): IngestResult {
  if (pagesRaw.length === 0) {
    throw new MalformedTwoHousesError("PDF contained no extractable text");
  }

  const tzName = opts.defaultTimezone ?? DEFAULT_TIMEZONE;
  const timezoneAssumed = !opts.defaultTimezone;
  if (!IANAZone.isValidZone(tzName)) {
    throw new MalformedTwoHousesError(`Unknown timezone: ${tzName}`);
  }

  const combined = concatenatePages(pagesRaw);
  const { messages, keptAsBody } = parseMessages(combined);
  if (messages.length === 0) {
    throw new MalformedTwoHousesError(
      "No 2houses messages detected — likely wrong PDF format.",
    );
  }

  const { rows, metas, stats: baseStats } = buildRowsAndMetadata(
    messages,
    tzName,
    timezoneAssumed,
  );
  const stats: IngestStats = { ...baseStats, page_count: pagesRaw.length };
  const csv = rowsToCsv(rows);
  const case_meta = buildCaseMeta(opts.corpusId, stats);

  const warnings: string[] = [];
  if (timezoneAssumed) {
    warnings.push(
      `2houses carries no time zone — assumed ${tzName}; verify/correct`,
    );
  }
  if (keptAsBody.length > 0) {
    warnings.push(
      `${keptAsBody.length} header-shaped line(s) kept as body (look-alikes)`,
    );
  }

  return { rows, source_metas: metas, stats, csv, case_meta, warnings };
}

/**
 * Ingest a 2houses conversations PDF export into the canonical CSV shape.
 *
 * Throws MalformedTwoHousesError if the PDF doesn't contain any valid 2houses
 * message headers (likely wrong format).
 */
export async function ingestTwoHousesPdf(
  pdfData: ArrayBuffer | Uint8Array,
  opts: IngestOptions,
): Promise<IngestResult> {
  const pagesRaw = await extractPdfTextEol(pdfData, opts.onProgress);
  return ingestTwoHousesFromPages(pagesRaw, {
    corpusId: opts.corpusId,
    defaultTimezone: opts.defaultTimezone,
  });
}
